"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { Loader2, Search } from "lucide-react";

import { cn } from "@/utils/cn";
import { Input } from "@/components/ui/Input";
import { Command, CommandEmpty, CommandList } from "@/components/ui/Command";

const commandItemInteractionClass = [
  "[&_[cmdk-item]]:mx-1",
  "[&_[cmdk-item]]:my-0.5",
  "[&_[cmdk-item]]:rounded-md",
  "[&_[cmdk-item]]:border",
  "[&_[cmdk-item]]:border-transparent",
  "[&_[cmdk-item]]:transition-all",
  "[&_[cmdk-item]]:duration-200",
  "[&_[cmdk-item]]:ease-in-out",
  "[&_[cmdk-item]:hover]:translate-x-1",
  "[&_[cmdk-item]:hover]:border-border",
  "[&_[cmdk-item]:hover]:bg-muted",
  "[&_[cmdk-item]:hover]:text-foreground",
  "[&_[cmdk-item]:hover]:shadow-sm",
  "[&_[cmdk-item][aria-selected=true]]:translate-x-1",
  "[&_[cmdk-item][aria-selected=true]]:border-primary/30",
  "[&_[cmdk-item][aria-selected=true]]:bg-accent",
  "[&_[cmdk-item][aria-selected=true]]:text-accent-foreground",
  "[&_[cmdk-item][aria-selected=true]]:shadow-sm",
  "[&_[cmdk-item][data-disabled=true]]:translate-x-0",
  "[&_[cmdk-item][data-disabled=true]]:border-transparent",
  "[&_[cmdk-item][data-disabled=true]]:shadow-none",
].join(" ");

const OPEN_RESULTS_MAX_HEIGHT = 360;

export type AsyncSearchComboboxProps = {
  className?: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading?: boolean;
  searching?: boolean;
  emptyMessage?: React.ReactNode;
  loadingMessage?: React.ReactNode;
  searchingMessage?: React.ReactNode;
  addonBefore?: React.ReactNode;
  inputAddonAfter?: React.ReactNode;
  heightClassName?: string;
  surfaceClassName?: string;
  inputClassName?: string;
  listClassName?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
  onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onListScroll?: React.UIEventHandler<HTMLDivElement>;
};

export const AsyncSearchCombobox = React.forwardRef<
  HTMLInputElement,
  AsyncSearchComboboxProps
>(
  (
    {
      className,
      placeholder,
      value,
      onValueChange,
      open,
      onOpenChange,
      loading = false,
      searching = false,
      emptyMessage = "No results found.",
      loadingMessage = "Loading...",
      searchingMessage = "Searching...",
      addonBefore,
      inputAddonAfter,
      heightClassName = "h-[42px]",
      surfaceClassName,
      inputClassName,
      listClassName,
      footer,
      children,
      onInputKeyDown,
      onListScroll,
    },
    ref,
  ) => {
    const anchorRef = React.useRef<HTMLDivElement | null>(null);
    const closedInputRef = React.useRef<HTMLInputElement | null>(null);
    const openInputRef = React.useRef<HTMLInputElement | null>(null);
    const [surfaceWidth, setSurfaceWidth] = React.useState<number | null>(null);
    const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
    const [heightExpanded, setHeightExpanded] = React.useState(false);

    React.useImperativeHandle(
      ref,
      () =>
        (open ? openInputRef.current : closedInputRef.current) as HTMLInputElement,
      [open],
    );

    const updateAnchorGeometry = React.useCallback(() => {
      const el = anchorRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      setSurfaceWidth(Math.round(rect.width));
      setAnchorRect(rect);
    }, []);

    React.useLayoutEffect(() => {
      const el = anchorRef.current;
      if (!el) return;

      updateAnchorGeometry();

      const ro = new ResizeObserver(() => updateAnchorGeometry());
      ro.observe(el);
      return () => ro.disconnect();
    }, [updateAnchorGeometry]);

    React.useLayoutEffect(() => {
      if (!open) return;

      updateAnchorGeometry();
      window.addEventListener("resize", updateAnchorGeometry);
      window.addEventListener("scroll", updateAnchorGeometry, true);

      return () => {
        window.removeEventListener("resize", updateAnchorGeometry);
        window.removeEventListener("scroll", updateAnchorGeometry, true);
      };
    }, [open, updateAnchorGeometry]);

    React.useEffect(() => {
      if (!open) {
        setHeightExpanded(false);
        return;
      }

      const frame = window.requestAnimationFrame(() => {
        setHeightExpanded(true);
        openInputRef.current?.focus();
      });

      return () => window.cancelAnimationFrame(frame);
    }, [open]);

    const busy = loading || searching;
    const searchIcon = busy ? (
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground pointer-events-none" />
    ) : (
      <Search className="h-4 w-4 text-muted-foreground pointer-events-none" />
    );

    const resolvedEmptyMessage = loading
      ? loadingMessage
      : searching
      ? searchingMessage
      : emptyMessage;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange(e.target.value);
      if (!open) onOpenChange(true);
    };

    return (
      <>
        {open &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              className="fixed inset-0 z-[240000] bg-overlay/25 backdrop-blur-[1px] animate-in fade-in-0 duration-150 dark:bg-overlay/55"
              onMouseDown={() => onOpenChange(false)}
            />,
            document.body,
          )}

        <div ref={anchorRef} className={className}>
          <Input
            ref={closedInputRef}
            value={value}
            placeholder={placeholder}
            onFocus={() => onOpenChange(true)}
            onChange={handleChange}
            tabIndex={open ? -1 : 0}
            aria-hidden={open}
            heightClassName={heightClassName}
            className={cn(
              "transition-[opacity,transform] duration-150 ease-out",
              open && "pointer-events-none scale-[0.99] opacity-0",
            )}
            addonAfter={inputAddonAfter}
            icon={searchIcon}
          />
        </div>

        {open &&
          anchorRect &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              className={cn(
                "fixed z-[250001] box-border origin-top overflow-hidden rounded-[10px] border border-border bg-popover text-popover-foreground shadow-md transition-[border-color,box-shadow] duration-200 ease-out hover:border-primary focus-within:border-primary dark:shadow-lg dark:shadow-overlay/30",
                surfaceClassName,
              )}
              style={{
                left: Math.round(anchorRect.left),
                top: Math.round(anchorRect.top),
                width: `${
                  surfaceWidth !== null
                    ? Math.round(surfaceWidth)
                    : Math.round(anchorRect.width)
                }px`,
              }}
            >
              <Input
                ref={openInputRef}
                value={value}
                placeholder={placeholder}
                onFocus={() => onOpenChange(true)}
                onChange={handleChange}
                heightClassName={heightClassName}
                wrapperClassName="relative z-[250002] [&>*]:border-0"
                className={cn(
                  "rounded-none bg-popover text-foreground shadow-none hover:border-transparent focus-visible:border-transparent",
                  inputClassName,
                )}
                addonBefore={addonBefore}
                addonAfter={inputAddonAfter}
                icon={searchIcon}
                onKeyDown={onInputKeyDown}
              />

              <div
                className={cn(
                  "w-full overflow-hidden border-t border-border bg-popover transition-[max-height,opacity] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]",
                  heightExpanded ? "opacity-100" : "opacity-0",
                )}
                style={{
                  maxHeight: heightExpanded ? `${OPEN_RESULTS_MAX_HEIGHT}px` : 0,
                }}
              >
                <Command
                  shouldFilter={false}
                  className={cn(
                    "rounded-none transition-transform duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]",
                    heightExpanded ? "translate-y-0" : "-translate-y-1",
                  )}
                >
                  <CommandList
                    className={cn(
                      "hide-scrollbar py-1",
                      commandItemInteractionClass,
                      listClassName,
                    )}
                    onScroll={onListScroll}
                  >
                    <CommandEmpty>{resolvedEmptyMessage}</CommandEmpty>
                    {children}
                  </CommandList>
                </Command>
              </div>
              {footer ? (
                <div className="sticky bottom-0 z-[250003] border-t border-border bg-popover px-2 py-2 shadow-[0_-4px_8px_-8px_hsl(var(--overlay)/0.25)] dark:shadow-[0_-4px_12px_-8px_hsl(var(--overlay)/0.55)]">
                  {footer}
                </div>
              ) : null}
            </div>,
            document.body,
          )}
      </>
    );
  },
);
AsyncSearchCombobox.displayName = "AsyncSearchCombobox";
